import { storeToRefs } from 'pinia';
import { useCustomerConfigStore } from '@peanuds/shared-state';

export type {
  CustomerConfig,
  CustomerCountries,
  CustomerCurrencyFormat,
  CustomerDateTime,
  CustomerLinks,
  CustomerMedia,
  CustomerNotifications,
  CustomerNovuContext,
  CustomerNovuContextValue,
  CustomerPromoMedia,
  CustomerPromoMediaType,
} from '@peanuds/shared-state';

/**
 * Reactive customer-specific configuration.
 *
 * Returns reactive refs to links and other customer-specific values.
 * Can be sourced from environment variables initially, and updated from
 * API later without component changes.
 *
 * @example
 * const { links } = useCustomerConfig();
 * // links.value.serviceAgreement is reactive
 *
 * @example
 * // In template
 * <a v-if="links.serviceAgreement" :href="links.serviceAgreement">...</a>
 */
export const useCustomerConfig = () => {
  const store = useCustomerConfigStore();
  return storeToRefs(store);
};

/**
 * Access to the full customer config store (for initialization and actions).
 *
 * Use this in main.ts or App.vue to initialize with env values
 * or fetch from API.
 *
 * @example
 * // Initialize in main.ts
 * const configStore = useCustomerConfigStore();
 * configStore.initialize({ links: { serviceAgreement: '...' } });
 *
 * @example
 * // Fetch from API later
 * await configStore.fetchFromApi();
 */
export { useCustomerConfigStore } from '@peanuds/shared-state';
